import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import {
  StandardEvents,
  ProductSelectEvent,
  CartLinesUpdateEvent,
  CartErrorEvent,
} from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id]
 * @property {string} [title]
 * @property {string} [name]
 * @property {boolean} [available]
 * @property {Object} [featured_media]
 * @property {Object} [featured_media.preview_image]
 * @property {string} [featured_media.preview_image.src]
 * @property {string} [featured_media.alt]
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} quantityDisplay
 * @property {HTMLElement} quantityNumber
 * @property {HTMLImageElement} productImage
 */

/**
 * Sticky add-to-cart component.
 *
 * The sticky bar is displayed only after the customer scrolls past the normal
 * product buy buttons and they are no longer visible above the viewport.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = [
    'stickyBar',
    'addToCartButton',
    'quantityDisplay',
    'quantityNumber',
  ];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');

    target?.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
      { signal }
    );

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartAddComplete,
      { signal }
    );

    document.addEventListener(
      StandardEvents.cartError,
      this.#handleCartAddComplete,
      { signal }
    );

    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#handleQuantityUpdate,
      { signal }
    );

    this.#getInitialQuantity();

    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;

      if (this.#isStuck && this.#isChatActive()) {
        this.#hideStickyBar();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();

    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Watches the normal buy-button area and controls the sticky bar.
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();

    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');

    if (!buyButtonsBlock) return;

    const footer =
      document.querySelector('footer') ??
      document.querySelector('[class*="footer-group"]');

    if (!footer) return;

    /*
     * Show the sticky bar only after the normal buy buttons have passed above
     * the viewport. Do not show it while the buttons are still below the
     * viewport and the customer has not reached them yet.
     */
    this.#buyButtonsIntersectionObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;

        if (!entry) return;

        const isBelowBuyButtons =
          buyButtonsBlock.getBoundingClientRect().bottom <= 0;

        if (
          !entry.isIntersecting &&
          isBelowBuyButtons &&
          !this.#isStuck
        ) {
          if (this.#hiddenByBottom || this.#isChatActive()) return;

          this.#showStickyBar();
        } else if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = false;
          this.#hideStickyBar();
        }
      }
    );

    /*
     * Hide the sticky bar near the footer so it does not cover footer content.
     */
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;

        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          const isBelowBuyButtons =
            buyButtonsBlock.getBoundingClientRect().bottom <= 0;

          if (isBelowBuyButtons) {
            this.#hiddenByBottom = false;

            if (!this.#isChatActive()) {
              this.#showStickyBar();
            }
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);

    this.#targetAddToCartButton = productForm.querySelector(
      '[ref="addToCartButton"]'
    );
  }

  /**
   * Handles clicks on the sticky add-to-cart button.
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();

    const cartIcon = document.querySelector(
      '.header-actions__cart-icon'
    );

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (
      !cartIcon ||
      !this.refs.addToCartButton ||
      !this.refs.productImage
    ) {
      return;
    }

    if (this.#resetTimeout) {
      clearTimeout(this.#resetTimeout);
    }

    const flyToCartElement =
      /** @type {FlyToCart} */ (
        document.createElement('fly-to-cart')
      );

    flyToCartElement.classList.add('fly-to-cart--sticky');

    flyToCartElement.style.setProperty(
      'background-image',
      `url(${this.refs.productImage.src})`
    );

    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([
      this.refs.addToCartButton,
      flyToCartElement,
    ]);

    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Updates the sticky bar when a product variant changes.
   *
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (
      !(event.target instanceof Element) ||
      event.target.closest('product-card')
    ) {
      return;
    }

    const { optionValueId } = event.detail ?? {};

    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const {
          html,
          productId,
          resource: variant,
        } = detail;

        if (
          productId &&
          productId !== this.dataset.productId
        ) {
          return;
        }

        const newStickyAddToCart =
          /** @type {HTMLElement | null} */ (
            html.querySelector('sticky-add-to-cart')
          );

        if (!newStickyAddToCart) return;

        const newStickyBar =
          newStickyAddToCart.querySelector(
            '[ref="stickyBar"]'
          );

        if (!newStickyBar) return;

        const currentStuck =
          this.refs.stickyBar.getAttribute('data-stuck') ||
          'false';

        const variantAvailable =
          newStickyAddToCart.dataset.variantAvailable;

        morph(
          this.refs.stickyBar,
          newStickyBar,
          { childrenOnly: true }
        );

        this.refs.stickyBar.setAttribute(
          'data-stuck',
          currentStuck
        );

        this.dataset.variantAvailable = variantAvailable;

        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        const productForm = this.#getProductForm();

        if (productForm) {
          this.#targetAddToCartButton =
            productForm.querySelector(
              '[ref="addToCartButton"]'
            );
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }

        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[sticky-add-to-cart] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * Updates the selected variant title when unavailable.
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';

    const variantTitleElement = this.querySelector(
      '.sticky-add-to-cart__variant'
    );

    const productId = this.dataset.productId;

    const variantPicker = document.querySelector(
      `variant-picker[data-product-id="${productId}"]`
    );

    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(
      variantPicker.querySelectorAll('input:checked')
    )
      .map(
        (option) =>
          /** @type {HTMLInputElement} */ (option).value
      )
      .filter((value) => value !== '')
      .join(' / ');

    if (!selectedOptions) return;

    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Resets the puppet flag after a cart request finishes.
   *
   * @param {CartLinesUpdateEvent | CartErrorEvent} event
   */
  #handleCartAddComplete = (event) => {
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    if (
      'promise' in event &&
      event.promise instanceof Promise
    ) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Updates the sticky quantity display.
   *
   * @param {QuantitySelectorUpdateEvent} event
   */
  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar.
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;

    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar.
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;

    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  /**
   * Returns whether Shopify Chat is active.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) {
      return false;
    }

    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Finds the main product form for this section.
   *
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;

    if (!productId) return null;

    const sectionElement =
      this.closest('.shopify-section');

    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace(
      'shopify-section-',
      ''
    );

    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Reads the initial selected quantity.
   */
  #getInitialQuantity() {
    this.#currentQuantity =
      parseInt(this.dataset.initialQuantity || '1') || 1;

    this.#updateButtonText();
  }

  /**
   * Updates the sticky button’s quantity display.
   */
  #updateButtonText() {
    const {
      addToCartButton,
      quantityDisplay,
      quantityNumber,
    } = this.refs;

    const available = !addToCartButton.disabled;

    quantityNumber.textContent =
      this.#currentQuantity.toString();

    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    StickyAddToCartComponent
  );
}